import React, { useState, useEffect } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import styled from 'styled-components';
import { useZone } from '../context/ZoneContext';
import { useWeather } from '../context/WeatherContext';
import { useRecommendation } from '../context/RecommendationContext';
import Alert from '../components/Alert';
import Loading from '../components/Loading';
import Button from '../components/Button';
import WeatherWidget from '../components/WeatherWidget';
import RecommendationList from '../components/RecommendationList';

const ZoneDetailContainer = styled.div`
  display: grid;
  grid-template-columns: repeat(3, 1fr);
  gap: 20px;
`;

const WeatherContainer = styled.div`
  grid-column: span 3;
  margin-bottom: 20px;
`;

const Card = styled.div`
  background-color: white;
  border-radius: 8px;
  box-shadow: 0 1px 3px rgba(0, 0, 0, 0.1);
  padding: 20px;
`;

const CardHeader = styled.div`
  display: flex;
  justify-content: space-between;
  align-items: center;
  margin-bottom: 15px;
`;

const CardTitle = styled.h2`
  font-size: 1.1rem;
  font-weight: 600;
  color: #1e293b;
  margin: 0;
`;

const CardContent = styled.div``;

const ZoneInfoCard = styled(Card)`
  grid-column: span 1;
`;

const InfoList = styled.div`
  display: flex;
  flex-direction: column;
  gap: 10px;
`;

const InfoItem = styled.div`
  display: flex;
  justify-content: space-between;
  padding: 8px 0;
  border-bottom: 1px solid #f1f5f9;
  
  &:last-child {
    border-bottom: none;
  }
`;

const InfoLabel = styled.span`
  font-size: 0.875rem;
  color: #64748b;
`;

const InfoValue = styled.span`
  font-size: 0.875rem;
  font-weight: 500;
  color: #1e293b;
`;

const MetricsCard = styled(Card)`
  grid-column: span 2;
`;

const MetricsTabs = styled.div`
  display: flex;
  border-bottom: 1px solid #e2e8f0;
  margin-bottom: 20px;
`;

const Tab = styled.button`
  padding: 10px 15px;
  background: none;
  border: none;
  font-size: 0.875rem;
  font-weight: ${props => props.$active ? '600' : '400'};
  color: ${props => props.$active ? '#3b82f6' : '#64748b'};
  cursor: pointer;
  border-bottom: 2px solid ${props => props.$active ? '#3b82f6' : 'transparent'};
  transition: all 0.2s;
  
  &:hover {
    color: ${props => props.$active ? '#3b82f6' : '#1e293b'};
  }
`;

const MetricChart = styled.div`
  height: 250px;
  background-color: #f8fafc;
  border-radius: 6px;
  display: flex;
  align-items: center;
  justify-content: center;
  margin-bottom: 20px;
`;

const MetricSummary = styled.div`
  display: grid;
  grid-template-columns: repeat(3, 1fr);
  gap: 15px;
`;

const MetricItem = styled.div`
  background-color: #f8fafc;
  border-radius: 6px;
  padding: 15px;
  text-align: center;
`;

const MetricValue = styled.div`
  font-size: 1.5rem;
  font-weight: 600;
  color: #1e293b;
  margin-bottom: 5px;
`;

const MetricLabel = styled.div`
  font-size: 0.75rem;
  color: #64748b;
`;

const RecommendationsContainer = styled.div`
  grid-column: span 3;
`;



const ZoneDetail = () => {
  const { zoneId } = useParams();
  const navigate = useNavigate();
  const [activeMetricTab, setActiveMetricTab] = useState('moisture');
  const { state, getZoneById, deleteZone } = useZone();
  const { currentZone, loading, error, notification } = state;
  const { setSelectedZone } = useWeather();
  const { 
    state: { recommendations, loading: recommendationsLoading, error: recommendationsError },
    loadRecommendations, 
    createTaskFromRecommendation, 
    dismissRecommendation 
  } = useRecommendation();
  
  // Fetch zone data when component mounts or zoneId changes
  useEffect(() => {
    const fetchZoneData = async () => {
      if (zoneId) {
        await getZoneById(zoneId);
        setSelectedZone(zoneId); // Set selected zone for weather context
        loadRecommendations(zoneId); // Load recommendations for this zone
      }
    };
    
    fetchZoneData();
    
    // We're intentionally not including getZoneById, setSelectedZone, and loadRecommendations
    // in the dependency array to prevent infinite loops. These functions come from context
    // providers and are stable in functionality even if their references change.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [zoneId]);
  
  // Handle zone deletion
  const handleDeleteZone = async () => {
    if (window.confirm('Are you sure you want to delete this zone?')) {
      await deleteZone(zoneId);
      navigate('/zones');
    }
  };
  
  // Mock data for metrics and recommendations
  // In a real app, these would come from API calls or be part of the zone data
  const metrics = {
    moisture: {
      current: '65%',
      optimal: '70%',
      min: '60%',
      chart: 'Moisture Chart Placeholder'
    },
    temperature: {
      current: '22.5°C',
      optimal: '23°C',
      min: '18°C',
      chart: 'Temperature Chart Placeholder'
    },
    nutrients: {
      nitrogen: '120 ppm',
      phosphorus: '35 ppm',
      potassium: '180 ppm',
      chart: 'Nutrients Chart Placeholder'
    }
  };
  
  
  const renderMetricContent = () => {
    switch (activeMetricTab) {
      case 'moisture':
        return (
          <>
            <MetricChart>{metrics.moisture.chart}</MetricChart>
            <MetricSummary>
              <MetricItem>
                <MetricValue>{metrics.moisture.current}</MetricValue>
                <MetricLabel>Current</MetricLabel>
              </MetricItem>
              <MetricItem>
                <MetricValue>{metrics.moisture.optimal}</MetricValue>
                <MetricLabel>Optimal</MetricLabel>
              </MetricItem>
              <MetricItem>
                <MetricValue>{metrics.moisture.min}</MetricValue>
                <MetricLabel>Minimum</MetricLabel>
              </MetricItem>
            </MetricSummary>
          </>
        );
      case 'temperature':
        return (
          <>
            <MetricChart>{metrics.temperature.chart}</MetricChart>
            <MetricSummary>
              <MetricItem>
                <MetricValue>{metrics.temperature.current}</MetricValue>
                <MetricLabel>Current</MetricLabel>
              </MetricItem>
              <MetricItem>
                <MetricValue>{metrics.temperature.optimal}</MetricValue>
                <MetricLabel>Optimal</MetricLabel>
              </MetricItem>
              <MetricItem>
                <MetricValue>{metrics.temperature.min}</MetricValue>
                <MetricLabel>Minimum</MetricLabel>
              </MetricItem>
            </MetricSummary>
          </>
        );
      case 'nutrients':
        return (
          <>
            <MetricChart>{metrics.nutrients.chart}</MetricChart>
            <MetricSummary>
              <MetricItem>
                <MetricValue>{metrics.nutrients.nitrogen}</MetricValue>
                <MetricLabel>Nitrogen</MetricLabel>
              </MetricItem>
              <MetricItem>
                <MetricValue>{metrics.nutrients.phosphorus}</MetricValue>
                <MetricLabel>Phosphorus</MetricLabel>
              </MetricItem>
              <MetricItem>
                <MetricValue>{metrics.nutrients.potassium}</MetricValue>
                <MetricLabel>Potassium</MetricLabel>
              </MetricItem>
            </MetricSummary>
          </>
        );
      default:
        return null;
    }
  };
  
  if (loading) {
    return <Loading />;
  }
  
  if (error) {
    return <Alert type="error" message={error} dismissible />;
  }
  
  if (!currentZone) {
    return <Alert type="warning" message="Zone not found" dismissible />;
  }
  
  return (
    <ZoneDetailContainer>
      {notification && (
        <Alert 
          type={notification.type} 
          message={notification.message} 
          dismissible 
        />
      )}
      
      <WeatherContainer>
        <WeatherWidget zoneId={zoneId} />
      </WeatherContainer>
      
      <ZoneInfoCard>
        <CardHeader>
          <CardTitle>Zone Information</CardTitle>
          <div>
            <Button 
              $size="small" 
              onClick={() => navigate(`/zones/edit/${zoneId}`)}
              style={{ marginRight: '8px' }}
            >
              Edit
            </Button>
            <Button 
              $size="small" 
              variant="danger" 
              onClick={handleDeleteZone}
            >
              Delete
            </Button>
          </div>
        </CardHeader>
        <CardContent>
          <InfoList>
            <InfoItem>
              <InfoLabel>Name</InfoLabel>
              <InfoValue>{currentZone.name}</InfoValue>
            </InfoItem>
            <InfoItem>
              <InfoLabel>Area</InfoLabel>
              <InfoValue>{currentZone.area}</InfoValue>
            </InfoItem>
            <InfoItem>
              <InfoLabel>Variety</InfoLabel>
              <InfoValue>{currentZone.variety}</InfoValue>
            </InfoItem>
            <InfoItem>
              <InfoLabel>Planting Date</InfoLabel>
              <InfoValue>{currentZone.plantingDate}</InfoValue>
            </InfoItem>
            <InfoItem>
              <InfoLabel>Soil Type</InfoLabel>
              <InfoValue>{currentZone.soilType}</InfoValue>
            </InfoItem>
            <InfoItem>
              <InfoLabel>Status</InfoLabel>
              <InfoValue>{currentZone.status}</InfoValue>
            </InfoItem>
            <InfoItem>
              <InfoLabel>Last Irrigation</InfoLabel>
              <InfoValue>{currentZone.lastIrrigation || 'N/A'}</InfoValue>
            </InfoItem>
            <InfoItem>
              <InfoLabel>Last Fertilization</InfoLabel>
              <InfoValue>{currentZone.lastFertilization || 'N/A'}</InfoValue>
            </InfoItem>
          </InfoList>
        </CardContent>
      </ZoneInfoCard>
      
      <MetricsCard>
        <CardHeader>
          <CardTitle>Metrics</CardTitle>
        </CardHeader>
        <CardContent>
          <MetricsTabs>
            <Tab 
              $active={activeMetricTab === 'moisture'} 
              onClick={() => setActiveMetricTab('moisture')}
            >
              Soil Moisture
            </Tab>
            <Tab 
              $active={activeMetricTab === 'temperature'} 
              onClick={() => setActiveMetricTab('temperature')}
            >
              Temperature
            </Tab>
            <Tab 
              $active={activeMetricTab === 'nutrients'} 
              onClick={() => setActiveMetricTab('nutrients')}
            >
              Nutrients
            </Tab>
          </MetricsTabs>
          
          {renderMetricContent()}
        </CardContent>
      </MetricsCard>
      
      <RecommendationsContainer>
        <RecommendationList
          recommendations={recommendations}
          zones={[currentZone]}
          onCreateTask={createTaskFromRecommendation}
          onDismiss={dismissRecommendation}
          loading={recommendationsLoading}
          error={recommendationsError}
        />
      </RecommendationsContainer>
    </ZoneDetailContainer>
  );
};

export default ZoneDetail;
